import React, { useEffect, useState } from 'react';
import { AlertCircle, ArrowRight, CheckCircle, Inbox, Layers, Loader, MapPin } from 'lucide-react';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import { consolidationService } from '../../../../../services/consolidationService';
import { FreeUpCandidate } from '../../../../../types/consolidation';
import { ConfirmLine } from '../types';
import { OpenCapacityArgs } from '../useCapacityUpdate';

interface FreeUpViewProps {
    warehouseID: string;
    /** To første tegn av sist skannede lokasjon — null når ingenting er skannet. */
    nearPrefix: string | null;
    /** Bumpes av ConsolidationApp etter utført flytt → re-laster listen. */
    refreshToken: number;
    onPick: (line: ConfirmLine) => void;
    /** Åpne «Oppdater kapasitet» for foreslått destinasjon + vare. */
    onUpdateCapacity?: (args: OpenCapacityArgs) => void;
}

/**
 * Visning C — «Frigjør nå» (kilde-først).
 * Topp-N nesten-tomme lokasjoner, sortert nær operatøren (prefiks fra sist
 * skannede lokasjon) eller for hele lageret. Trykk på rad → bekreftelsesark
 * med én linje.
 */
export const FreeUpView: React.FC<FreeUpViewProps> = ({
    warehouseID,
    nearPrefix,
    refreshToken,
    onPick,
    onUpdateCapacity
}) => {
    const { t } = useTranslation();
    const [wholeWarehouse, setWholeWarehouse] = useState<boolean>(!nearPrefix);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [candidates, setCandidates] = useState<FreeUpCandidate[]>([]);

    const effectivePrefix = wholeWarehouse ? undefined : nearPrefix ?? undefined;

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const result = await consolidationService.getFreeUpCandidates(warehouseID, effectivePrefix, 25);
                if (!cancelled) setCandidates(result);
            } catch (err) {
                if (!cancelled) {
                    setError(err instanceof Error ? err.message : t('warehouse.modules.consolidation.errors.loadFailed', {}, 'Kunne ikke laste data'));
                    setCandidates([]);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [warehouseID, effectivePrefix, refreshToken]);

    const handlePick = (candidate: FreeUpCandidate) => {
        onPick({
            id: `${candidate.itemNo}|${candidate.binCode}|${candidate.suggestedToBinCode || ''}`,
            fromBinCode: candidate.binCode,
            toBinCode: candidate.suggestedToBinCode || '',
            originalToBinCode: candidate.suggestedToBinCode || '',
            itemNo: candidate.itemNo,
            description1: candidate.description1,
            quantity: String(candidate.quantity),
            maxQuantity: candidate.quantity,
            freesBin: true,
            capacityConfidence: candidate.capacityConfidence,
            status: 'pending'
        });
    };

    return (
        <div className="space-y-3">
            {/* Nærhets-chip + Hele lageret-toggle */}
            <div className="flex flex-wrap items-center gap-2">
                {nearPrefix && (
                    <button
                        onClick={() => setWholeWarehouse(false)}
                        className={`px-4 py-2 rounded-full text-sm font-medium min-h-[44px] transition-colors ${
                            !wholeWarehouse
                                ? 'bg-cyan-500 text-white'
                                : 'bg-white border border-gray-300 text-gray-600 hover:bg-gray-50'
                        }`}
                    >
                        {t('warehouse.modules.consolidation.freeUp.near', { '0': nearPrefix }, `Nær: reol ${nearPrefix}`)}
                    </button>
                )}
                <button
                    onClick={() => setWholeWarehouse(true)}
                    className={`px-4 py-2 rounded-full text-sm font-medium min-h-[44px] transition-colors ${
                        wholeWarehouse || !nearPrefix
                            ? 'bg-cyan-500 text-white'
                            : 'bg-white border border-gray-300 text-gray-600 hover:bg-gray-50'
                    }`}
                >
                    {t('warehouse.modules.consolidation.freeUp.wholeWarehouse', {}, 'Hele lageret')}
                </button>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-12 bg-white rounded-lg border shadow-sm">
                    <Loader className="animate-spin h-6 w-6 text-cyan-500" />
                    <span className="ml-2 text-sm text-gray-600">
                        {t('warehouse.modules.consolidation.freeUp.loading', {}, 'Henter kandidater…')}
                    </span>
                </div>
            ) : error ? (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start text-sm text-red-800">
                    <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
                    <span className="break-words">{error}</span>
                </div>
            ) : candidates.length === 0 ? (
                <div className="bg-white rounded-lg p-6 shadow-sm border text-center">
                    <Inbox className="h-10 w-10 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">
                        {t('warehouse.modules.consolidation.freeUp.empty', {}, 'Ingen frigjør-kandidater funnet')}
                    </p>
                </div>
            ) : (
                <div className="space-y-2">
                    {candidates.map((candidate, index) => (
                        <div
                            key={`${candidate.binCode}|${candidate.itemNo}|${index}`}
                            className="w-full bg-white rounded-lg p-3 shadow-sm border hover:border-cyan-300 hover:shadow-md transition-all"
                        >
                            <button
                                onClick={() => handlePick(candidate)}
                                className="w-full text-left min-h-[44px]"
                            >
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2">
                                        <MapPin className="h-4 w-4 text-cyan-500 flex-shrink-0" />
                                        <span className="font-mono font-semibold text-gray-900 break-all">
                                            {candidate.binCode}
                                        </span>
                                    </div>
                                    <div className="text-xs text-gray-600 break-words mt-0.5">
                                        <span className="font-mono">{candidate.itemNo}</span>
                                        {candidate.description1 && <span> · {candidate.description1}</span>}
                                    </div>
                                    <div className="text-xs text-gray-500 mt-0.5">
                                        {t('warehouse.modules.consolidation.freeUp.quantity', { '0': candidate.quantity }, `Antall: ${candidate.quantity}`)}
                                    </div>
                                </div>
                                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                                    {candidate.suggestedToBinCode ? (
                                        <div className="flex items-center gap-1 text-sm text-gray-800">
                                            <ArrowRight className="h-4 w-4 text-gray-400" />
                                            <span className="font-mono">{candidate.suggestedToBinCode}</span>
                                            {candidate.destinationQuantity !== undefined && candidate.destinationQuantity !== null && (
                                                <span className="text-xs text-gray-500 whitespace-nowrap">
                                                    — {t('warehouse.modules.consolidation.freeUp.existingQty', { '0': candidate.destinationQuantity }, `${candidate.destinationQuantity} fra før`)}
                                                </span>
                                            )}
                                        </div>
                                    ) : (
                                        <span className="text-xs text-gray-500">
                                            {t('warehouse.modules.consolidation.freeUp.noDestination', {}, 'Ingen forslag — velg destinasjon manuelt')}
                                        </span>
                                    )}
                                    {candidate.guaranteedFit ? (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded-full whitespace-nowrap">
                                            <CheckCircle className="h-3 w-3" />
                                            {t('warehouse.modules.consolidation.freeUp.guaranteed', {}, '1 flytt = 1 frigjort')}
                                        </span>
                                    ) : (
                                        <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs rounded-full whitespace-nowrap">
                                            {candidate.capacityConfidence === 'estimated'
                                                ? t('warehouse.modules.consolidation.freeUp.approxCapacity', {}, 'ca. kapasitet')
                                                : t('warehouse.modules.consolidation.freeUp.confirmManually', {}, 'bekreft manuelt')}
                                        </span>
                                    )}
                                </div>
                            </div>
                            </button>
                            {onUpdateCapacity && candidate.suggestedToBinCode && !candidate.guaranteedFit && (
                                <button
                                    onClick={() => onUpdateCapacity({
                                        binCode: candidate.suggestedToBinCode!,
                                        itemNo: candidate.itemNo,
                                        itemDescription: candidate.description1,
                                        currentQuantity: candidate.destinationQuantity ?? undefined
                                    })}
                                    className="mt-2 flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium text-cyan-700 border border-cyan-200 hover:bg-cyan-50 min-h-[36px]"
                                    title={t('warehouse.modules.consolidation.capacity.updateHint', {}, 'Det er plass? Oppdater kapasiteten')}
                                >
                                    <Layers className="h-3.5 w-3.5" />
                                    {t('warehouse.modules.consolidation.capacity.update', {}, 'Oppdater kapasitet')}
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};
